import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Sidebar.module.css';
import NavItem from './NavItem';
import SidebarPinToggle from './SidebarPinToggle';
import { SidebarItem, sidebarItems } from './sidebarData';
import useFundContext from '@/contexts/Fund/useFundContext';
import useAuthContext from '@/contexts/Auth/useAuthContext';
import useFeatureFlags from '@/contexts/FeatureFlags/useFeatureFlags';
import useFundAccess from '@/hooks/useFundAccess';
import { canOpenIn } from '@/helpers/permissionWorlds';
import icComingSoon from '@/assets/icons/coming-soon.svg';
import externalLink from '@/assets/icons/external-link.svg';

/** The site's route allowlist, the same source NavItem uses to decide what is live. */
const enabledRoutes: string[] = import.meta.env.VITE_APP_ENABLED_ROUTES ?? [];

/** האתר הציבורי של מרכז הצדקה. ריק באתרים שאין להם אתר אב (bro2bro, hatzdakaUS). */
const PARENT_SITE_URL = import.meta.env.VITE_PARENT_SITE_URL;

/**
 * תפריט אזור הקרן — ניווט בלבד.
 *
 * בורר הקרן ישב כאן בראש התפריט, ועבר לפס שבראש כל מסך: שם הוא זמין גם
 * כשהתפריט מכווץ לרייל וגם בנייד, שבו התפריט הוא מגירה סגורה כמעט תמיד.
 * ראה FundBalanceBar.
 */
interface SidebarProps {
  isExpanded: boolean;
  isPinned: boolean;
  isRail: boolean;
  onTogglePinned: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isExpanded, isPinned, isRail, onTogglePinned }) => {
  const { currentFund } = useFundContext();
  const { t } = useTranslation();
  const { isCurrentUserAdmin, isCurrentUserDebugger, currentUser } = useAuthContext();
  const isAdmin: boolean = isCurrentUserAdmin();
  const isDebugger: boolean = isCurrentUserDebugger();
  const currentEmail = currentUser?.email?.trim().toLowerCase();
  const isFeatureComingSoonEnabled = import.meta.env.VITE_APP_ENABLE_COMING_SOON === 'true';
  const { isEnabled } = useFeatureFlags();
  // Permission worlds (Druk, 22.10.2026): a screen in a world closed to this coordinator is not offered.
  const { worlds } = useFundAccess(currentFund?.id);

  // Filter sidebar items based on role. adminOnly/debuggerOnly act as a whitelist of
  // allowed roles (OR'd together when both are set), not independent AND gates.
  // Feature-flag gating is handled in NavItem (blurred + non-clickable), not by hiding —
  // except for an item that asked to be hidden outright, see hideWhenFeatureOff.
  const filteredSidebarItems = useMemo(() => {
    const isVisible = (item: SidebarItem): boolean => {
      // allowedEmails הוא שער חותך: פריט שיש לו רשימה נראה רק למי שברשימה,
      // גם אם הוא Admin. משמש למסכים שחיים באתר אבל עוד בבדיקת עיצוב.
      if (item.allowedEmails && !(currentEmail && item.allowedEmails.includes(currentEmail))) return false;
      // פריט שביקש לא להופיע כלל באתר שהנתיב שלו כבוי, במקום להופיע מטושטש
      // ולא לחיץ. ראה hideWhenRouteDisabled.
      if (item.hideWhenRouteDisabled && item.to && !enabledRoutes.includes(item.to)) return false;
      /*
       * פריט שביקש להיעלם כשהדגל שלו כבוי, במקום להופיע מטושטש. ראה
       * hideWhenFeatureOff. מוסתר גם בזמן שהמפה עוד נטענת - זה הכיוון הבטוח:
       * עדיף שפריט יופיע רגע באיחור מאשר שפיצ'ר שהוסר יהבהב על המסך.
       */
      if (item.hideWhenFeatureOff && item.featureKey && !isEnabled(item.featureKey)) return false;
      // פריט שמייצג את המצב הישן ומפנה את מקומו כשאחד הדגלים החדשים נפתח. ראה hideWhenAnyFeatureOn.
      if (item.hideWhenAnyFeatureOn?.some((key) => isEnabled(key))) return false;
      // והפריט שתופס את מקומו: מופיע רק כשאחד הדגלים האלה נפתח. ראה hideUnlessAnyFeatureOn.
      if (item.hideUnlessAnyFeatureOn && !item.hideUnlessAnyFeatureOn.some((key) => isEnabled(key))) return false;
      if (item.to && !canOpenIn(worlds, item.to)) return false;
      if (!item.adminOnly && !item.debuggerOnly) return true;
      if (item.adminOnly && isAdmin) return true;
      if (item.debuggerOnly && isDebugger) return true;
      return false;
    };
    const filterItems = (items: SidebarItem[]): SidebarItem[] => {
      return items
        .filter(isVisible)
        .map((item) => ({
          ...item,
          children: item.children ? filterItems(item.children) : undefined,
        }))
        // קבוצה שכל הפריטים שלה סוננו הייתה נשארת כחץ פתיחה מעל כלום.
        .filter((item) => !item.children || item.children.length > 0);
    };
    return filterItems(sidebarItems);
  }, [isAdmin, isDebugger, currentEmail, isEnabled, worlds]);

  const renderNavItem = (item: SidebarItem, index: number | string) => {
    return (
      <NavItem
        sideBarItem={item}
        key={index}
        to={
          item.to?.startsWith('/')
            ? item.to
            : currentFund?.id != null
              ? `/${currentFund.id}/${item.to}`
              : undefined
        }
        isCollapsed={!isExpanded}
        isRail={isRail}
        onRequestExpand={onTogglePinned}
      >
        {item.children?.map((child, childIndex) => renderNavItem(child, `${index}-${childIndex}`))}
      </NavItem>
    );
  };

  return (
    <div className={styles.panel}>
      <div className={styles.pinToggle}>
        <SidebarPinToggle isPinned={isPinned} onToggle={onTogglePinned} />
      </div>
      <nav className={styles.nav} aria-label="Main Navigation">
        {/* רק הפריטים גוללים. הקישור שמתחת יושב מחוץ לאזור הגלילה, ולכן הוא
            נצמד לתחתית המסך ולא זז כשהעמוד נגלל (HAT-1374). */}
        <div className={`${styles.navScroll} ${styles.sideMenuScrollBar}`}>
          <div className={styles.navItems}>
            {filteredSidebarItems.map((item, index) => renderNavItem(item, index))}
          </div>
        </div>

        {/* הדרך החוצה, בתחתית התפריט ולא בהדר: היא שייכת לניווט, וכאן היא גם
            מגיעה לנייד — בהדר היא הוסתרה שם מחוסר מקום. */}
        {PARENT_SITE_URL && (
          <a
            className={`${styles.navItem} ${styles.regularNavItem} ${styles.parentSiteLink}`}
            href={PARENT_SITE_URL}
            target="_blank"
            rel="noopener noreferrer"
            title={t('toParentSite')}
          >
            <span className={styles.parentSiteIcon}>
              <img src={externalLink} alt="" aria-hidden="true" />
            </span>
            <span className={styles.navLabel}>{t('toParentSite')}</span>
          </a>
        )}

        {isFeatureComingSoonEnabled && isExpanded && !isAdmin && (
          <div className={styles.comingSoon}>
            <img src={icComingSoon} alt="coming soon" className="mb-3" />
            <span className={`${styles.comingSoonText} mb-4`}>{t('comingSoon')}...</span>
            <span className={styles.comingSoonSubtext}>{t('newSystemOnTheWay')},</span>
            <span className={styles.comingSoonSubtext2}>{t('stayTuned')}</span>
          </div>
        )}
      </nav>
    </div>
  );
};

export default Sidebar;